import { AppAction } from "@Safemate/Store/types";
import {call, debounce, put, select, take, takeLatest} from 'redux-saga/effects';
import * as Routines from "../routines";
import * as Api from "../api";
import { feedbackFailure } from '../../../Feedback/reducer';
import { Transaction } from "@Safemate/Model/Transaction";
import { TransactionEventFilterEnum } from "@Safemate/TransactionLog/TransactionEnum";
import { selectDates, selectDevice, selectFilter, selectLastUpdate, selectPage, selectTransactions } from "../selectors";
import { TransactionLogActionTypes } from "../actionTypes";
import { getLocation, getLocations, RowsPerPage } from "@Safemate/TransactionLog/utils";
import { TransactionDevice } from "@Safemate/Model/TransactionDevice";
import { Battery, Signal } from "../types";

function* getTransactions(){

  try {

    const filter: TransactionEventFilterEnum[] = yield select(selectFilter);
    const { fromDate, toDate } = yield select(selectDates);
    const { id } = yield select(selectDevice);

    const data: Api.GetTransactions = {
      fromDate: fromDate.format("DD-MM-YYYYTHH:mm"),
      toDate: toDate.format("DD-MM-YYYYTHH:mm"),
      filter,
      deviceId: id
    }

    const currentUpdate = Date.now();

    const transactions: Transaction[] = yield call(Api.getTransactions, data);
    yield put(Routines.getTransactions.success({transactions, lastUpdate: currentUpdate}));
  }
  catch(e){
    console.log(e)
    yield feedbackFailure(e);
    yield put(Routines.getTransactions.failure());
  }
  yield put(Routines.getTransactions.fulfill());
}

function* pollTransactions(){
  try{
    const filter: TransactionEventFilterEnum[] = yield select(selectFilter);
    const device: TransactionDevice = yield select(selectDevice)
    const existingTransactions: Transaction[] = yield select(selectTransactions);
    const latestTx = existingTransactions[0];
    const latestTxId = latestTx.id || 0;

    const currentUpdate = Date.now();
    const { toDate, fromDate } = yield select(selectDates);
    const transactions: Transaction[] = yield call(Api.pollTransactions, device.id, toDate.format("DD-MM-YYYYTHH:mm"), filter, fromDate.format("DD-MM-YYYYTHH:mm"), latestTxId);

    const newIds = new Set(transactions.map(tx => tx.id));

    const result = [
      ...transactions.map(tx => {
        return {...tx, new: true}
      }),
      ...existingTransactions.filter(tx => !newIds.has(tx.id)).map(tx => {
        return {...tx, new: false}
      })
    ].filter((value, index, self) =>
      index === self.findIndex((t) => t.id === value.id)
    );

    yield put(Routines.getTransactions.success({transactions: result, lastUpdate: currentUpdate}));

  }
  catch(e){
    yield put(Routines.pollTransactions.failure())
  }
}

function* evaluatePosition(action: AppAction<number>){


  try {

    const txId  = action.payload;

    let transactions: Transaction[] = yield select(selectTransactions);

    const editedTx = transactions.find(tx => tx.id === txId);

    if(editedTx){
      yield call(getLocation, editedTx, false);
      transactions = transactions.map(tx => {
        if(tx.id === txId){
          return editedTx;
        }
        return tx;
      })
    }

    yield put(Routines.evaluatePosition.success(transactions));
  }
    
  catch(e){
    console.log(e)
    yield feedbackFailure(e);
    yield put(Routines.evaluatePosition.failure());
  }
  yield put(Routines.evaluatePosition.fulfill());
}

function* getBatteryForRange(action: AppAction<number>){
  try {
    const battery: Battery[] = yield call(Api.getBatteryForRange, action.payload);
    yield put(Routines.getBatteryForRange.success(battery));
  }
  catch(e){
    yield feedbackFailure(e);
    yield put(Routines.getBatteryForRange.failure());
  }
  yield put(Routines.getBatteryForRange.fulfill());
}

function* getSignalForRange(action: AppAction<number>){
  try {
    const signal: Signal[] = yield call(Api.getSignalForRange, action.payload);
    yield put(Routines.getSignalForRange.success(signal));
  }
  catch(e){
    yield feedbackFailure(e);
    yield put(Routines.getSignalForRange.failure());
  }
  yield put(Routines.getSignalForRange.fulfill());
}

function* logTxLogAccess(action: AppAction<number>){
  try{
    yield call(Api.logTxLogAccess, action.payload);
    yield put(Routines.logTxLogAccess.success());
  }
  catch(e){
    yield put(Routines.logTxLogAccess.failure());
  }
}

function* transactionSaga(){
  yield takeLatest(Routines.evaluatePosition.TRIGGER, evaluatePosition);
  yield takeLatest(Routines.getDevice.SUCCESS, getTransactions);
  yield takeLatest(Routines.getTransactions.TRIGGER, getTransactions);
  yield takeLatest(Routines.pollTransactions.TRIGGER, pollTransactions);
  yield debounce(1000, TransactionLogActionTypes.SET_FILTER, getTransactions);
  yield debounce(1000, TransactionLogActionTypes.SET_DATE, getTransactions);
  yield takeLatest(Routines.getBatteryForRange.TRIGGER, getBatteryForRange);
  yield takeLatest(Routines.getSignalForRange.TRIGGER, getSignalForRange);
  yield takeLatest(Routines.logTxLogAccess.TRIGGER, logTxLogAccess);
}

export default transactionSaga;
